// =============================================================================
// fileTypeGuard.ts — content-aware upload validation for USER file uploads
// =============================================================================
// Only PDF / JPG / PNG are accepted on the user upload surfaces (request
// documents, invoices, per-child fee + device attachments, the feedback
// attachment). Each file is checked on BOTH its extension AND its magic bytes,
// and the two must AGREE — so a file with no extension, a disallowed type, or a
// renamed/mismatched file (e.g. an .exe renamed to .pdf) is rejected.
//
// Deliberately NOT wired into Resources, CRM, supplier logos or DFV-service docs
// (John, 4 Sep 2026) — those keep their existing behaviour.
//
// ⚠️ This validates a file's TYPE, not its safety. A genuinely malicious but
// structurally valid PDF still passes. Real malware scanning would be a separate
// server-side step (e.g. ClamAV / a scanning service on the stored object).
// =============================================================================

export type FileKind = "pdf" | "png" | "jpeg";

// The picker hint for the file inputs (not enforcement — the checks below are).
export const UPLOAD_ACCEPT = ".pdf,.png,.jpg,.jpeg";

const CONTENT_TYPE: Record<FileKind, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpeg: "image/jpeg",
};

export function contentTypeFor(kind: FileKind): string {
  return CONTENT_TYPE[kind];
}

// The file extension mapped to our canonical kind, or "" when the name has no
// extension or an extension we don't accept.
export function extKind(name: string | null | undefined): FileKind | "" {
  const s = String(name || "");
  const dot = s.lastIndexOf(".");
  const ext = dot >= 0 ? s.slice(dot + 1).toLowerCase() : "";
  if (ext === "pdf") return "pdf";
  if (ext === "png") return "png";
  if (ext === "jpg" || ext === "jpeg") return "jpeg";
  return "";
}

// The file's real kind from its leading magic bytes, or "" if unrecognised.
// Reads defensively — a short buffer just fails to match (never throws).
export function sniffMagic(bytes: Uint8Array | null | undefined): FileKind | "" {
  if (!bytes || bytes.length < 3) return "";
  // PDF: "%PDF-" → 25 50 44 46 2D
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return "pdf";
  }
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "png";
  }
  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "jpeg";
  }
  return "";
}

export type UploadDecision =
  | { ok: true; kind: FileKind; contentType: string }
  | { ok: false; error: string };

// The single reject message (John, 4 Sep 2026: point the user at the extension).
export function rejectMessage(name?: string | null): string {
  const n = String(name || "").trim();
  const label = n ? `"${n}"` : "That file";
  return `${label} couldn't be uploaded. Only PDF, JPG and PNG files are accepted — please check the file's extension and try again.`;
}

// Pure decision: allowed only when the extension is one we accept, the content is
// one we recognise, AND they are the same kind.
export function decideUpload(
  ext: FileKind | "",
  sniff: FileKind | "",
  name?: string | null
): UploadDecision {
  if (ext && sniff && ext === sniff) {
    return { ok: true, kind: ext, contentType: CONTENT_TYPE[ext] };
  }
  return { ok: false, error: rejectMessage(name) };
}

// A File/Blob-like input: anything with a name and a slice() whose result exposes
// arrayBuffer(). A real browser File satisfies this (File.slice returns a Blob),
// and it keeps the helper testable with a lightweight stand-in.
interface UploadFileLike {
  name?: string | null;
  slice?: (start: number, end: number) => { arrayBuffer: () => Promise<ArrayBuffer> };
}

// Validate a file before upload. Reads the first 16 bytes to sniff the content,
// then applies decideUpload. Never throws.
//
// If the bytes cannot be inspected at all (no arrayBuffer / an empty read — e.g. a
// non-browser or test environment), fall back to the extension allowlist so
// legitimate uploads still work. Real browsers always expose arrayBuffer, so the
// strict extension-AND-content check applies there.
export async function validateUploadFile(file: UploadFileLike | null): Promise<UploadDecision> {
  if (!file) return { ok: false, error: rejectMessage(null) };
  const ext = extKind(file.name);
  let sniff: FileKind | "" = "";
  let read = false;
  try {
    const head = typeof file.slice === "function" ? file.slice(0, 16) : null;
    if (head && typeof head.arrayBuffer === "function") {
      const buf = new Uint8Array(await head.arrayBuffer());
      if (buf.length > 0) {
        read = true;
        sniff = sniffMagic(buf);
      }
    }
  } catch {
    read = false;
  }
  if (!read) {
    return ext
      ? { ok: true, kind: ext, contentType: CONTENT_TYPE[ext] }
      : { ok: false, error: rejectMessage(file.name) };
  }
  return decideUpload(ext, sniff, file.name);
}
